import { nextTick, onMounted, ref } from "vue";
import { toColumns } from "@/utils/gridColumns";

/**
 * Splits `items` into balanced columns using each item's real rendered height, measured once
 * after mount — a static schema-based weight guess can't know about per-instance runtime content
 * (e.g. how many rows a saved list already has), so any such guess is only ever approximate.
 *
 * Renders once with `initialWeightOf` (defaults to plain round-robin) so there's something
 * reasonable on screen immediately, measures every item's mounted DOM height, then flips
 * `columns` to the real-height-balanced split — one reflow on initial load, never again: the
 * split is frozen after that single measurement, so columns don't reshuffle as the user edits a
 * field afterward (that continuous-rebalancing behavior is what breaks a CSS multi-column
 * masonry layout — see toColumns's docs).
 *
 * @param {any[]} items - Items to distribute.
 * @param {(item: any) => string} keyOf - Stable identity per item, matching its template `:key`.
 * @param {Object} [options]
 * @param {number} [options.columnCount=2] - Number of columns.
 * @param {(item: any) => number} [options.initialWeightOf] - Weight for the pre-measurement
 *   render. Defaults to uniform (plain round-robin).
 * @returns {{
 *   columns: import("vue").Ref<any[][]>,
 *   setItemRef: (el: Element | { $el: Element } | null, item: any) => void,
 * }} `columns` is reactive — read it directly in the template. `setItemRef` is a Vue function-ref
 *   callback: bind it as `:ref="el => setItemRef(el, item)"` on each rendered item's root element
 *   (or component — its exposed `$el` is used automatically).
 * @example
 * const { columns, setItemRef } = useMeasuredColumns(ADMIN_SECTIONS, section => section.key);
 * // <RfDashboardCard v-for="section in columns[0]" :ref="el => setItemRef(el, section)" ... />
 */
export const useMeasuredColumns = (items, keyOf, { columnCount = 2, initialWeightOf } = {}) => {
  const columns = ref(toColumns(items, columnCount, initialWeightOf));
  const elements = new Map();

  const setItemRef = (el, item) => {
    // A component ref yields its public instance, not the DOM node — .$el is Vue's standard way
    // to reach the real element from either.
    const node = el?.$el ?? el;
    if (node) elements.set(keyOf(item), node);
    else elements.delete(keyOf(item));
  };

  onMounted(async () => {
    await nextTick();
    const heightOf = item => elements.get(keyOf(item))?.offsetHeight ?? 0;
    columns.value = toColumns(items, columnCount, heightOf);
  });

  return { columns, setItemRef };
};
